import { useSeo } from "../hooks/useSeo";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useCart, formatHuf } from "../context/CartContext";

// Relative URL — calls same origin the page was served from (preview, production, custom domain all work).
const API_BASE = "";

export default function CheckoutSuccess() {
  useSeo({
    title: "Sikeres rendelés | Hair Elixir Hungary",
    description: "",
    noindex: true,
  });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Clear cart immediately on success page load
    try {
      clear();
      localStorage.removeItem("he-cart-v1");
    } catch (e) {
      // ignore
    }
  }, [clear]);

  useEffect(() => {
    if (!sessionId) {
      setError("Hiányzó session_id");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/checkout/session/${encodeURIComponent(sessionId)}`);
        if (!res.ok) throw new Error("not_found");
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError("A munkamenet nem található.");
      }
    })();
  }, [sessionId]);

  const orderNumber = sessionId ? sessionId.slice(-8).toUpperCase() : "—";

  return (
    <main data-testid="page-checkout-success" className="pt-32 pb-32 he-container min-h-[60vh]">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--he-gold)]/15 text-[color:var(--he-gold)] mb-8">
          <Check size={28} />
        </div>
        <p className="overline mb-5">Sikeres tranzakció</p>
        <h1
          className="font-serif-display tracking-[-0.022em] leading-[1.06]"
          style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)", fontWeight: 400 }}
        >
          Köszönjük a <span className="italic text-[color:var(--he-gold)]">vásárlást.</span>
        </h1>
        <p className="mt-6 text-[color:var(--he-ink-soft)] font-light leading-[1.85]">
          A rendelésed beérkezett. A visszaigazoló e-mailt rövidesen megkapod
          a megadott címre — minden részlettel együtt. Kérdés esetén írj a
          hairelixirhungary@gmail.com címre.
        </p>

        <div className="mt-12 border border-[color:var(--he-line)] bg-[color:var(--he-cream)] p-7 md:p-9 text-left">
          <div className="flex items-baseline justify-between mb-4">
            <span className="overline-xs text-[color:var(--he-ink-soft)]">Rendelésszám</span>
            <span className="font-serif-display text-xl" data-testid="success-order-number">
              {orderNumber}
            </span>
          </div>

          {error && (
            <p className="text-sm text-[color:var(--he-ink-soft)]">{error}</p>
          )}

          {data && (
            <>
              <div className="flex items-baseline justify-between mb-4">
                <span className="overline-xs text-[color:var(--he-ink-soft)]">Végösszeg</span>
                <span className="font-serif-display text-2xl" data-testid="success-total">
                  {data.amount_total != null
                    ? formatHuf(Math.round(data.amount_total / 100))
                    : "—"}{" "}
                  {data.currency || ""}
                </span>
              </div>
              {data.customer_email && (
                <div className="flex items-baseline justify-between mb-4">
                  <span className="overline-xs text-[color:var(--he-ink-soft)]">E-mail</span>
                  <span className="font-light text-sm">{data.customer_email}</span>
                </div>
              )}
              {data.shipping_address?.line1 && (
                <div className="border-t border-[color:var(--he-line)] pt-5 mt-5">
                  <p className="overline-xs text-[color:var(--he-ink-soft)] mb-2">Szállítási cím</p>
                  <p className="font-light text-sm leading-relaxed">
                    {data.shipping_address.country} · {data.shipping_address.postal_code}{" "}
                    {data.shipping_address.city}
                    <br />
                    {data.shipping_address.line1}
                    {data.shipping_address.line2 ? ` · ${data.shipping_address.line2}` : ""}
                  </p>
                </div>
              )}
              <div className="border-t border-[color:var(--he-line)] pt-5 mt-5">
                <span className="overline-xs text-[color:var(--he-ink-soft)]">Fizetés státusza</span>{" "}
                <span className="font-serif-display text-sm">{data.payment_status || "—"}</span>
              </div>
            </>
          )}
        </div>

        <Link
          to="/termekek"
          className="mt-10 inline-flex btn-outline-gold"
          data-testid="success-back-to-shop"
        >
          <span>Vissza a webshopba</span>
          <ArrowRight size={14} />
        </Link>
      </div>
    </main>
  );
}
